import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { fmtLocalDateTime } from '../../../utils/datetime';
import { sameWordingOf } from './messageLibraryModel';
import type {
  MessageDetail, MessageHistoryRow, MessageRow, MessageUsageRow,
} from './types';
import './messageLibrary.css';

/**
 * The read-only reference panels under the detail form: where a message is
 * raised, which other messages say the same thing, and its change history.
 *
 * All three answer the same question in different ways — "what else does editing
 * this affect?" — which is why they sit together rather than being scattered.
 *
 * ── Every panel takes `detail: MessageDetail | null` ──────────────────────────
 * `null` means the record is still being created. Each panel then draws its
 * GRID, headed and empty, with a note saying why. Showing the columns matters:
 * they tell you what the tab will contain once the message exists, and a tab
 * that is a bare sentence before saving and a table afterwards reads as two
 * different features (user request 2026-09-29).
 */

/** These three panels show NO note while the record does not exist yet (user
 *  request 2026-09-29). The grid is already headed and already says "No data to
 *  display." — a sentence repeating that, above an obviously empty table, is
 *  noise. Notes that carry real information are kept: how widely a saved message
 *  is used, and why a saved one has no call sites. */
const NOTE = 'ml-tabnote';

/** Where the legacy code raises this message — one row per screen.
 *
 *  A bare list of screen names cannot answer the question the panel exists for:
 *  WHICH module owns each screen, and how heavily it leans on the message. Both
 *  facts are already in the scan, so they belong in columns beside the screen. */
export function UsedInPanel({ detail }: { detail: MessageDetail | null }) {
  const rows = detail?.usedIn ?? [];

  const columns: AppColumn<MessageUsageRow>[] = [
    { field: 'module', header: 'Module', width: 200, render: (r) => r.module },
    { field: 'bo', header: 'BO', width: 260, render: (r) => r.bo },
    { field: 'calls', header: 'Calls', width: 90, align: 'right' },
  ];

  // The modules actually named, so the note can say "across 2 modules" rather
  // than the vaguer "more than one".
  const modules = new Set(rows.map((r) => r.module).filter(Boolean));

  const note = !detail
    ? null                       // new record — the empty grid speaks for itself
    : rows.length === 0
      ? 'No call site found in the legacy ALPACA source. The key may be unused, or it may be raised through a variable the scan cannot resolve.'
      : null;

  return (
    <>
      {note && <p className={NOTE}>{note}</p>}
      {detail && rows.length > 0 && (
          <p className="ml-tabnote">
            Raised {detail!.usedInSites} time{detail!.usedInSites === 1 ? '' : 's'} in code, on{' '}
            {rows.length} screen{rows.length === 1 ? '' : 's'}
            {modules.size > 1
              ? ` across ${modules.size} modules — a shared message.`
              : modules.size === 1
                ? ` in ${[...modules][0]}.`
                : '.'}
          </p>
      )}

      <AppDataGrid<MessageUsageRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => `${r.module}|${r.bo}`}
        maximizable
        exportFilename="message-used-in"
        defaultPageSize={10}
        emptyText="No data to display."
      />

      {(detail?.usedInFiles.length ?? 0) > 0 && (
        <p className="ml-tabfiles">
          <b>Source files ({detail!.usedInFiles.length}):</b>{' '}
          {detail!.usedInFiles.join(' · ')}
        </p>
      )}
    </>
  );
}

/** Other live messages whose wording is identical. */
export function SameWordingPanel({
  detail, rows,
}: { detail: MessageDetail | null; rows: MessageRow[] }) {
  const others = sameWordingOf(detail, rows);

  const columns: AppColumn<MessageRow>[] = [
    { field: 'msid', header: 'MSID', width: 80 },
    { field: 'messageKey', header: 'Message Key', width: 240 },
    { field: 'module', header: 'Module', width: 160, render: (r) => r.module ?? r.derivedModule ?? '' },
  ];

  const note = !detail
    ? null                       // new record — the empty grid speaks for itself
    : others.length === 0
      ? 'No other message uses this wording.'
      : null;

  return (
    <>
      {note && <p className={NOTE}>{note}</p>}
      {detail && others.length > 0 && (
          <p className="ml-tabnote">
            {others.length} other message{others.length === 1 ? '' : 's'} say
            {others.length === 1 ? 's' : ''} exactly this. One shared <code>Common.*</code> key
            would keep them in step; today each has to be edited separately.
          </p>
      )}
      <AppDataGrid<MessageRow>
        rows={others}
        columns={columns}
        getRowId={(r) => r.oid}
        maximizable
        exportFilename="message-same-wording"
        defaultPageSize={10}
        emptyText="No data to display."
      />
    </>
  );
}

/**
 * This message's audit trail — who changed what, when, and why.
 *
 * It reads the SHARED audit endpoint rather than a bespoke one (standing rule 8),
 * so the rows here are the very same rows the Data Center audit log and the
 * toolbar's Audit button show — there is no second version of the truth to drift.
 *
 * The fetch lives in the panel rather than beside the record load because the tab
 * is usually never opened: most messages have never been edited, so loading the
 * history for every detail view would be a request per open for nothing.
 */
export function ChangeHistoryPanel({
  detail, rows,
}: { detail: MessageDetail | null; rows: MessageHistoryRow[] }) {
  const oid = detail?.oid ?? null;
  const error: string | null = null;

  const columns: AppColumn<MessageHistoryRow>[] = [
    {
      field: 'createdDate',
      header: 'Date/Time',
      width: 150,
      text: (r) => fmtLocalDateTime(r.createdDate),
      render: (r) => fmtLocalDateTime(r.createdDate),
    },
    { field: 'createdBy', header: 'User', width: 130, render: (r) => r.createdBy ?? '' },
    { field: 'operationType', header: 'Operation', width: 110, render: (r) => r.operationType ?? '' },
    { field: 'propertyName', header: 'Property', width: 150, render: (r) => r.propertyName ?? '' },
    { field: 'oldValue', header: 'Old Value', width: 220, render: (r) => r.oldValue ?? '' },
    { field: 'newValue', header: 'New Value', width: 220, render: (r) => r.newValue ?? '' },
    { field: 'comment', header: 'Reason', width: 180, render: (r) => r.comment ?? '' },
  ];

  const note = error
    ? error
    : !oid
      ? null                     // new record — the empty grid speaks for itself
      : rows.length === 0
        ? `No changes recorded yet${detail?.lastUpdatedDate
          ? `. This message was last updated on ${fmtLocalDateTime(detail.lastUpdatedDate)}.`
          : '.'}`
        : null;

  return (
    <>
      {note && <p className={NOTE}>{note}</p>}
      <AppDataGrid<MessageHistoryRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.oid}
        maximizable
        exportFilename="message-change-history"
        defaultPageSize={10}
        emptyText="No data to display."
      />
    </>
  );
}
